import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useObservable } from 'rxjs-hooks'

import { InfoRounded } from '@mui/icons-material'
import { Chip, Stack, Tooltip, Typography } from '@mui/material'
import { useAuthService, useSubjectSelector } from '@procom-labs/common'

import { useVerifyEmail } from '@resume-portal/features/resume-builder/hooks'
import { useRemainingTrialDays } from '@resume-portal/hooks'
import { AccessTypes, UserOrganizationStatuses } from '@resume-portal/models'
import { CommonRoutesPaths } from '@resume-portal/routes/routes.enum'
import { userOrganizationStore } from '@resume-portal/stores'

import { RoundButton } from '../ui/round-button'

export const UsageStatus: React.FC<{ isATSBullhorn: boolean }> = ({
  isATSBullhorn,
}) => {
  const navigate = useNavigate()
  const { handleSendVerificationEmail } = useVerifyEmail()
  const { remainingTrialDays } = useRemainingTrialDays()
  const authService = useAuthService()
  const { t } = useTranslation('main')
  const currentUser = useObservable(() => authService.currentUser$)
  const { remainingUsage, userOrganization, isPendingJoin, userOrgAccessType } =
    useSubjectSelector(userOrganizationStore, [
      'remainingUsage',
      'userOrganization',
      'isPendingJoin',
      'userOrgAccessType',
    ])

  const isDummyOrg = useMemo(
    () =>
      currentUser?.isEmailVerified &&
      userOrganization?.status === UserOrganizationStatuses.PendingActivation,
    [currentUser?.isEmailVerified, userOrganization?.status]
  )

  const usageLabel = useMemo(() => {
    let prefix = `${remainingUsage?.remaining}/${remainingUsage?.total} ${t(
      'usage.remaining'
    )}`

    if (remainingTrialDays !== null && remainingTrialDays >= 0) {
      prefix = `${remainingUsage?.remaining}/${remainingUsage?.total} ${t(
        'usage.remainingWithTrialDays',
        { days: remainingTrialDays }
      )}`
    }

    if (
      (currentUser?.isEmailVerified && isDummyOrg && isPendingJoin) ||
      userOrgAccessType !== AccessTypes.Admin
    ) {
      return prefix
    }
    return `${prefix}. ${t('usage.wantMore')}`
  }, [
    currentUser?.isEmailVerified,
    isDummyOrg,
    isPendingJoin,
    remainingUsage?.remaining,
    remainingUsage?.total,
    t,
    userOrgAccessType,
    remainingTrialDays,
  ])

  if (remainingUsage && (!isDummyOrg || isPendingJoin)) {
    return (
      <Stack
        gap={2}
        direction="row"
        alignItems="center"
        sx={{
          pr: 4,
          borderRight: 'solid 1px',
          borderColor: 'common.white',
        }}
      >
        <Typography variant="body2">{usageLabel}</Typography>
        {isPendingJoin ? (
          <Tooltip title={t('usage.pendingTooltip')}>
            <Chip
              sx={{
                background: '#F0F3F9',
                borderRadius: '4px',
                border: 'solid 1px rgba(107, 137, 196, 0.6)',
              }}
              icon={
                <InfoRounded
                  sx={{
                    color: (theme) =>
                      `${theme.palette.gorillaResume?.dark} !important`,
                  }}
                />
              }
              variant="filled"
              label={t('usage.pendingOrg')}
            />
          </Tooltip>
        ) : (
          ((userOrganization?.status === UserOrganizationStatuses.Active &&
            userOrgAccessType === AccessTypes.Admin) ||
            (!currentUser?.isEmailVerified && !isATSBullhorn)) && (
            <RoundButton
              size="small"
              variant="outlined"
              sx={{
                color: 'common.white',
                borderColor: 'rgba(255, 255, 255, 0.75) !important',
                fontSize: '14px !important',
                px: 1.5,
              }}
              onClick={
                !currentUser?.isEmailVerified && !isATSBullhorn
                  ? handleSendVerificationEmail
                  : () =>
                      navigate(CommonRoutesPaths.PricingAndPlans, {
                        state: { canGoBack: true },
                      })
              }
            >
              {!currentUser?.isEmailVerified && !isATSBullhorn
                ? t('usage.verify')
                : t('usage.upgradeNow')}
            </RoundButton>
          )
        )}
      </Stack>
    )
  }
  return <></>
}
